import { useTranslation } from 'react-i18next';
import {
  UserIcon,
  EnvelopeIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '../ui/Modal';
import { Spinner } from '../ui/Spinner';
import { formatRelativeTime } from '../../utils/dateFormat';
import type { FormResponse, FormStructure, FormQuestion, ChoiceOption } from '@affogato/shared';
import clsx from 'clsx';

interface ResponseDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  response: FormResponse | null;
  formStructure: FormStructure | null;
  isLoading?: boolean;
}

function formatAnswer(
  question: FormQuestion,
  answer: unknown
): React.ReactNode {
  if (answer === undefined || answer === null) {
    return <span className="text-gray-400 dark:text-slate-500 italic">Sin respuesta</span>;
  }

  switch (question.type) {
    case 'scale': {
      const value = Number(answer);
      const min = question.scaleConfig?.min ?? 1;
      const max = question.scaleConfig?.max ?? 5;
      const percentage = ((value - min) / (max - min)) * 100;

      return (
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                value <= 2 ? 'bg-red-500' : value <= 3 ? 'bg-yellow-500' : 'bg-green-500'
              )}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <span className="font-semibold text-gray-900 dark:text-white min-w-[3rem] text-right">
            {value} / {max}
          </span>
        </div>
      );
    }

    case 'yes_no': {
      const isYes = answer === true || answer === 'yes' || answer === 'sí';
      return (
        <span
          className={clsx(
            'inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium',
            isYes
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          )}
        >
          {isYes ? 'Sí' : 'No'}
        </span>
      );
    }

    case 'text':
      return (
        <p className="text-gray-700 dark:text-slate-300 whitespace-pre-wrap">
          {String(answer)}
        </p>
      );

    case 'single_choice': {
      const selectedOption = question.choices?.find(
        (c: ChoiceOption) => c.id === answer || c.text === answer
      );
      return (
        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-lg text-sm font-medium">
          <CheckCircleIcon className="w-4 h-4" />
          {selectedOption?.text || String(answer)}
        </span>
      );
    }

    case 'multiple_choice': {
      const selectedIds = Array.isArray(answer) ? answer : [answer];
      const selectedOptions = question.choices?.filter(
        (c: ChoiceOption) => selectedIds.includes(c.id) || selectedIds.includes(c.text)
      ) || [];

      if (selectedOptions.length === 0) {
        return <span className="text-gray-400 dark:text-slate-500 italic">Sin respuesta</span>;
      }

      return (
        <div className="flex flex-wrap gap-2">
          {selectedOptions.map((option) => (
            <span
              key={option.id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-lg text-sm font-medium"
            >
              <CheckCircleIcon className="w-3.5 h-3.5" />
              {option.text}
            </span>
          ))}
        </div>
      );
    }

    default:
      return <span className="text-gray-700 dark:text-slate-300">{String(answer)}</span>;
  }
}

export function ResponseDetailModal({
  isOpen,
  onClose,
  response,
  formStructure,
  isLoading = false,
}: ResponseDetailModalProps) {
  const { t } = useTranslation('diagnostics');

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('responses.viewResponse') || 'Ver respuesta'}
      size="2xl"
    >
      {isLoading || !response ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
          {/* Respondent Info */}
          <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t('responses.respondentInfo') || 'Información del respondiente'}
              </h3>
              <span
                className={clsx(
                  'px-2 py-0.5 rounded-full text-xs font-medium',
                  response.status === 'completed'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                )}
              >
                {response.status === 'completed'
                  ? t('responses.completed') || 'Completado'
                  : t('responses.inProgress') || 'En progreso'}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {response.respondentName && (
                <div className="flex items-center gap-2 text-sm">
                  <UserIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-slate-400">
                    {response.respondentName}
                  </span>
                </div>
              )}
              {response.respondentEmail && (
                <div className="flex items-center gap-2 text-sm">
                  <EnvelopeIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-slate-400">
                    {response.respondentEmail}
                  </span>
                </div>
              )}
              {response.respondentRole && (
                <div className="flex items-center gap-2 text-sm">
                  <BriefcaseIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-slate-400">
                    {response.respondentRole}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                {response.status === 'completed' ? (
                  <>
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span className="text-gray-600 dark:text-slate-400">
                      {formatRelativeTime(response.completedAt || response.updatedAt)}
                    </span>
                  </>
                ) : (
                  <>
                    <ClockIcon className="w-4 h-4 text-yellow-500" />
                    <span className="text-gray-600 dark:text-slate-400">
                      {t('responses.startedAt') || 'Iniciado'} {formatRelativeTime(response.createdAt)}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Answers by Section */}
          {formStructure?.sections.map((section) => {
            const sectionAnswers = section.questions.filter(
              (q) => response.answers[q.id] !== undefined
            );

            // Only show sections that have at least one answer
            if (sectionAnswers.length === 0 && response.status === 'completed') {
              return null;
            }

            return (
              <div key={section.id} className="space-y-4">
                <div className="border-b border-gray-200 dark:border-slate-700 pb-2">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {section.title}
                  </h3>
                  {section.description && (
                    <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                      {section.description}
                    </p>
                  )}
                </div>

                <div className="space-y-4">
                  {section.questions
                    .sort((a, b) => a.order - b.order)
                    .map((question) => {
                      const answer = response.answers[question.id];

                      return (
                        <div
                          key={question.id}
                          className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4"
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                              {question.text}
                              {question.required && (
                                <span className="text-red-500 ml-1">*</span>
                              )}
                            </p>
                            <span className="text-xs text-gray-400 dark:text-slate-500 flex-shrink-0 capitalize">
                              {question.type.replace('_', ' ')}
                            </span>
                          </div>
                          {question.description && (
                            <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                              {question.description}
                            </p>
                          )}
                          <div className="mt-2">
                            {formatAnswer(question, answer)}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}

          {/* No answers state */}
          {(!formStructure || Object.keys(response.answers).length === 0) && (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-slate-400">
                {t('responses.noAnswers') || 'No hay respuestas registradas aún'}
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
